<!-- eslint-disable vue/no-v-html -->
<template>
  <div
    class="mb-4 flex"
    :class="[message.sender_type === ChatRole.USER ? 'justify-end' : 'justify-start']"
  >
    <!-- AI Message -->
    <template v-if="message.sender_type === ChatRole.AGENT">
      <div class="flex items-end">
        <div
          class="mr-2 mb-1 flex h-6 w-6 items-center justify-center rounded-full bg-gray-800 text-white"
        >
          <chat-icon :width="12" :height="12" />
        </div>
        <div class="max-w-[85%] rounded-tl-lg rounded-tr-lg rounded-br-lg bg-gray-100 px-4 py-2">
          <div v-if="getFileBlocks().length" class="mb-2 flex flex-wrap gap-2">
            <div
              v-for="fileBlock in getFileBlocks()"
              :key="fileBlock.file_id"
              class="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2"
            >
              <icon-document class="flex-shrink-0" />
              <span class="text-sm break-all text-gray-700">
                {{ fileBlock.file_name }}
              </span>
            </div>
          </div>
          <div
            v-if="parsedMessage"
            class="chat-content font-sans text-gray-800"
            v-html="parsedMessage"
          ></div>

          <span v-if="showTimestamp" class="mt-1 block text-xs text-gray-500">
            {{ formatDate(message.created_at, "h:mm a") }}
          </span>
        </div>
      </div>
    </template>

    <!-- User Message -->
    <template v-else>
      <div class="flex max-w-[85%] flex-col items-end">
        <div v-if="getFileBlocks().length" class="mb-2 flex flex-wrap gap-2">
          <div
            v-for="fileBlock in getFileBlocks()"
            :key="fileBlock.file_id"
            class="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2"
          >
            <icon-document class="flex-shrink-0 text-gray-600" />
            <span class="text-sm break-all text-gray-700">
              {{ fileBlock.file_name }}
            </span>
          </div>
        </div>
        <div
          v-if="parsedMessage || showTimestamp"
          class="rounded-tl-lg rounded-tr-lg rounded-bl-lg bg-gray-800 px-4 py-2 text-white"
        >
          <div v-if="parsedMessage" class="chat-content font-sans" v-html="parsedMessage"></div>
          <span v-if="showTimestamp" class="mt-1 block text-xs text-gray-300">
            {{ formatDate(message.created_at, "h:mm a") }}
          </span>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { marked } from "marked";
import DOMPurify from "dompurify";
import ChatIcon from "@/components/icons/IconChat.vue";
import IconDocument from "@/components/icons/IconDocument.vue";
import { ChatRole } from "@/api/http/schemas/chatSessionSchemas";
import { formatDate } from "@/util/formatting";
import type { ChatMessage } from "@/api/http/schemas/chatSessionSchemas";

const { message, showTimestamp } = defineProps<{
  message: ChatMessage;
  showTimestamp?: boolean;
}>();

const parsedMessage = computed(() => {
  const textBlocks = getTextBlocks();
  if (textBlocks.length) {
    const combinedText = textBlocks.join("\n\n");
    const rawHtml = marked.parse(combinedText) as string;
    return DOMPurify.sanitize(rawHtml);
  }
  return "";
});

const getTextBlocks = (): string[] => {
  const contentBlocks = message?.content?.content?.reduce<string[]>((acc, block) => {
    if (block.type === "text") {
      acc.push(block.text);
    }
    return acc;
  }, []);

  if (contentBlocks) {
    return contentBlocks;
  }

  if (message?.message) {
    return [message.message];
  }

  return [];
};

const getFileBlocks = () => {
  return (
    message?.content?.content?.reduce<{ file_id: number; file_name: string }[]>((acc, block) => {
      if (block.type === "file") {
        acc.push({ file_id: block.file_id, file_name: block.file_name });
      }
      return acc;
    }, []) ?? []
  );
};
</script>

<style scoped>
.chat-content :deep(p),
.chat-content :deep(div),
.chat-content :deep(span),
.chat-content :deep(strong),
.chat-content :deep(em),
.chat-content :deep(code),
.chat-content :deep(*) {
  word-break: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
}
</style>
